import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Key, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';

const SuccessWareCredentialsPage = () => {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [credentials, setCredentials] = useState({
    env: 'production',
    username: '',
    password: '',
    accessToken: '',
    tokenExpiresAt: ''
  });

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    if (!supabase) return;

    setLoading(true);
    try {
      // Get SuccessWare provider
      const { data: provider } = await supabase
        .from('integration_providers')
        .select('id')
        .eq('slug', 'successware')
        .single();

      if (!provider) {
        console.error('SuccessWare provider not found');
        return;
      }

      // Get credentials
      const { data: creds } = await supabase
        .from('integration_credentials')
        .select('*')
        .eq('provider_id', provider.id)
        .single();

      if (creds) {
        setCredentials({
          env: creds.env || 'production',
          username: creds.username || '',
          password: creds.password || '',
          accessToken: creds.access_token || '',
          tokenExpiresAt: creds.token_expires_at || ''
        });
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCredentials = async () => {
    if (!supabase) return;

    setSaving(true);
    try {
      // Get SuccessWare provider
      const { data: provider } = await supabase
        .from('integration_providers')
        .select('id')
        .eq('slug', 'successware')
        .single();

      if (!provider) {
        throw new Error('SuccessWare provider not found');
      }

      // Upsert credentials
      const { error } = await supabase
        .from('integration_credentials')
        .upsert({
          provider_id: provider.id,
          env: credentials.env,
          username: credentials.username,
          password: credentials.password,
          access_token: credentials.accessToken,
          token_expires_at: credentials.tokenExpiresAt || null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'provider_id,env'
        });

      if (error) throw error;

      alert('Credentials saved successfully');
    } catch (error: any) {
      console.error('Error saving credentials:', error);
      alert(`Failed to save: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const authenticate = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await fetch('https://publicapi.successwareg2.com/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Authentication failed');
      }

      // Store token
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 1); // Token valid for 1 hour

      setCredentials(prev => ({
        ...prev,
        accessToken: data.access_token,
        tokenExpiresAt: expiresAt.toISOString()
      }));

      setTestResult({
        success: true,
        message: 'Authentication successful! Token stored.'
      });

      // Auto-save after successful auth
      setTimeout(async () => {
        if (!supabase) return;

        const { data: provider } = await supabase
          .from('integration_providers')
          .select('id')
          .eq('slug', 'successware')
          .single();

        if (provider) {
          await supabase
            .from('integration_credentials')
            .upsert({
              provider_id: provider.id,
              env: credentials.env,
              username: credentials.username,
              password: credentials.password,
              access_token: data.access_token,
              token_expires_at: expiresAt.toISOString(),
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'provider_id,env'
            });
        }
      }, 100);

    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Authentication failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const testConnection = async () => {
    if (!credentials.accessToken) {
      setTestResult({
        success: false,
        message: 'No access token. Please authenticate first.'
      });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      // Test with minimal employees query
      const response = await fetch('https://publicapi.successwareg2.com/api/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${credentials.accessToken}`
        },
        body: JSON.stringify({
          query: `query MinimalEmployees { fetchEmployees(inactive: false) { id } }`
        })
      });

      const data = await response.json();

      if (data.errors) {
        throw new Error(data.errors[0]?.message || 'GraphQL query failed');
      }

      if (!response.ok) {
        throw new Error('Connection test failed');
      }

      const employeeCount = data.data?.fetchEmployees?.length || 0;

      setTestResult({
        success: true,
        message: `Connection successful! Found ${employeeCount} employees.`
      });

    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.message || 'Connection test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const isTokenExpired = () => {
    if (!credentials.tokenExpiresAt) return true;
    return new Date(credentials.tokenExpiresAt) < new Date();
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
          <Link to="/integrations" className="hover:text-white">Integrations</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/integrations/successware" className="hover:text-white">SuccessWare</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-white">Credentials</span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">SuccessWare Credentials</h1>
        <p className="text-slate-400">
          API authentication and environment configuration
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : (
        <div className="max-w-2xl space-y-6">
          {/* Environment */}
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Environment
            </label>
            <select
              value={credentials.env}
              onChange={(e) => setCredentials({ ...credentials, env: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
            >
              <option value="production">Production</option>
              <option value="sandbox">Sandbox</option>
            </select>
          </div>

          {/* Username */}
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Username
            </label>
            <input
              type="text"
              value={credentials.username}
              onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
              placeholder="API username"
            />
          </div>

          {/* Password */}
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 pr-12 text-white"
                placeholder="API password"
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Access Token Status */}
          {credentials.accessToken && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-slate-300">
                  Access Token
                </label>
                {isTokenExpired() ? (
                  <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded">
                    Expired
                  </span>
                ) : (
                  <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded">
                    Valid
                  </span>
                )}
              </div>
              <code className="block text-xs text-slate-400 bg-slate-950 p-3 rounded border border-slate-700 overflow-x-auto">
                {credentials.accessToken.substring(0, 50)}...
              </code>
              {credentials.tokenExpiresAt && (
                <p className="text-xs text-slate-500 mt-2">
                  Expires: {new Date(credentials.tokenExpiresAt).toLocaleString()}
                </p>
              )}
            </div>
          )}

          {/* Test Result */}
          {testResult && (
            <div className={`border rounded-lg p-4 ${
              testResult.success
                ? 'bg-green-500/10 border-green-500/30'
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-start gap-3">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                )}
                <div className="flex-1">
                  <p className={testResult.success ? 'text-green-300' : 'text-red-300'}>
                    {testResult.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={authenticate}
              disabled={testing || !credentials.username || !credentials.password}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Authenticating...
                </>
              ) : (
                <>
                  <Key className="w-4 h-4" />
                  Authenticate
                </>
              )}
            </button>

            <button
              onClick={testConnection}
              disabled={testing || !credentials.accessToken || isTokenExpired()}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors"
            >
              {testing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Test Connection
                </>
              )}
            </button>

            <button
              onClick={saveCredentials}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded-lg transition-colors ml-auto"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Credentials'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuccessWareCredentialsPage;
